import type { OriginalUnavailableReason } from '@lib/media/originalMedia';
import { Clip } from '@stores/MomentMerge/MomentMergeStore';
import Hls from 'hls.js';
import React from 'react';
import css from '../MomentMergeVideoPlayer.module.scss';
import { ClipStore, PlaybackSource } from '../types';
import { VideoComponent } from './VideoComponent';

type VideoWrapperProps = {
  video1Ref: React.RefObject<HTMLVideoElement | null>;
  video2Ref: React.RefObject<HTMLVideoElement | null>;
  hls1Ref: React.RefObject<Hls | null>;
  hls2Ref: React.RefObject<Hls | null>;
  activeVideo: 'video1' | 'video2';
  video1Clip: Clip | null;
  video2Clip: Clip | null;
  currentClipIndex: number;
  video1Loading: boolean;
  video2Loading: boolean;
  video1TargetTime: number | undefined;
  video2TargetTime: number | undefined;
  store: ClipStore;
  activeMomentId?: string | null;
  onVideo1TimeUpdate: () => void;
  onVideo2TimeUpdate: () => void;
  onVideoEnded: () => void;
  onVideo1Ready: () => void;
  onVideo2Ready: () => void;
  onVideo1LoadingChange: (loading: boolean) => void;
  onVideo2LoadingChange: (loading: boolean) => void;
  isPlaying: boolean;
  playbackSource: PlaybackSource;
  onOriginalUnavailable?: (reason: OriginalUnavailableReason) => void;
};

export const VideoWrapper: React.FC<VideoWrapperProps> = ({
  video1Ref,
  video2Ref,
  hls1Ref,
  hls2Ref,
  activeVideo,
  video1Clip,
  video2Clip,
  currentClipIndex,
  video1Loading,
  video2Loading,
  video1TargetTime,
  video2TargetTime,
  store,
  activeMomentId,
  onVideo1TimeUpdate,
  onVideo2TimeUpdate,
  onVideoEnded,
  onVideo1Ready,
  onVideo2Ready,
  onVideo1LoadingChange,
  onVideo2LoadingChange,
  isPlaying,
  playbackSource,
  onOriginalUnavailable,
}) => {
  // Create more stable keys that don't change during drag operations
  // Use a combination of clip ID and position to maintain stability
  const video1Key = video1Clip ? `video1-${video1Clip.id}` : 'video1-empty';
  const video2Key = video2Clip ? `video2-${video2Clip.id}` : 'video2-empty';

  return (
    <>
      {/* Video 1 */}
      <div className={css.videoWrapperDiv}>
        <VideoComponent
          key={video1Key}
          videoRef={video1Ref}
          hlsRef={hls1Ref}
          isActive={activeVideo === 'video1' && video1Clip !== null}
          isPaused={!isPlaying}
          clipData={video1Clip}
          currentClipIndex={currentClipIndex}
          onTimeUpdate={onVideo1TimeUpdate}
          onEnded={onVideoEnded}
          onReady={onVideo1Ready}
          onLoadingChange={onVideo1LoadingChange}
          store={store}
          targetTime={video1TargetTime}
          activeMomentId={activeMomentId}
          playbackSource={playbackSource}
          onOriginalUnavailable={onOriginalUnavailable}
        />
      </div>

      {/* Video 2 */}
      <div className={css.videoWrapperDiv}>
        <VideoComponent
          key={video2Key}
          videoRef={video2Ref}
          hlsRef={hls2Ref}
          isActive={activeVideo === 'video2' && video2Clip !== null}
          isPaused={!isPlaying}
          clipData={video2Clip}
          currentClipIndex={currentClipIndex}
          onTimeUpdate={onVideo2TimeUpdate}
          onEnded={onVideoEnded}
          onReady={onVideo2Ready}
          onLoadingChange={onVideo2LoadingChange}
          store={store}
          targetTime={video2TargetTime}
          activeMomentId={activeMomentId}
          playbackSource={playbackSource}
          onOriginalUnavailable={onOriginalUnavailable}
        />
      </div>
    </>
  );
};
